import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";

interface ClientSettingsRow {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  profile_status: string;
  dob: string | null;
  household_size: number | null;
  retirement_target_age: number | null;
  estimated_net_worth: number | null;
  onboarding_status: string;
  onboarding_step: number;
  tier_override: string | null;
}

export const Route = createFileRoute("/client/settings")({
  head: () => ({ meta: [{ title: "Settings — Family Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <ClientSettings />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function ClientSettings() {
  const { user } = useAuth();
  const [row, setRow] = useState<ClientSettingsRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    dob: "",
    household_size: "",
    retirement_target_age: "",
    estimated_net_worth: "",
  });

  useEffect(() => {
    if (!user) return;
    void loadSettings();
  }, [user?.id]);

  async function loadSettings() {
    if (!user) return;
    const [profileRes, clientRes] = await Promise.all([
      supabase.from("profiles").select("full_name, email, phone, status").eq("id", user.id).maybeSingle(),
      supabase.from("clients").select("dob, household_size, retirement_target_age, estimated_net_worth, onboarding_status, onboarding_step, tier_override").eq("user_id", user.id).maybeSingle(),
    ]);
    const next: ClientSettingsRow = {
      full_name: profileRes.data?.full_name ?? null,
      email: profileRes.data?.email ?? null,
      phone: profileRes.data?.phone ?? null,
      profile_status: profileRes.data?.status ?? "pending",
      dob: (clientRes.data as any)?.dob ?? null,
      household_size: clientRes.data?.household_size ?? null,
      retirement_target_age: (clientRes.data as any)?.retirement_target_age ?? null,
      estimated_net_worth: (clientRes.data as any)?.estimated_net_worth ?? null,
      onboarding_status: clientRes.data?.onboarding_status ?? "in_progress",
      onboarding_step: clientRes.data?.onboarding_step ?? 0,
      tier_override: clientRes.data?.tier_override ?? null,
    };
    setRow(next);
    setForm({
      full_name: next.full_name ?? "",
      phone: next.phone ?? "",
      dob: next.dob ?? "",
      household_size: next.household_size != null ? String(next.household_size) : "",
      retirement_target_age: next.retirement_target_age != null ? String(next.retirement_target_age) : "",
      estimated_net_worth: next.estimated_net_worth != null ? String(next.estimated_net_worth) : "",
    });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const profileUpdate = await supabase
        .from("profiles")
        .update({
          full_name: form.full_name || null,
          phone: form.phone || null,
        })
        .eq("id", user.id);
      if (profileUpdate.error) throw profileUpdate.error;

      const clientPayload = {
        user_id: user.id,
        dob: form.dob || null,
        household_size: form.household_size ? Number(form.household_size) : null,
        retirement_target_age: form.retirement_target_age ? Number(form.retirement_target_age) : null,
        estimated_net_worth: form.estimated_net_worth ? Number(form.estimated_net_worth) : null,
      };
      const clientUpsert = await supabase
        .from("clients")
        .upsert(clientPayload, { onConflict: "user_id" });
      if (clientUpsert.error) throw clientUpsert.error;

      toast.success("Profile updated. Your Strategy Center is personalized.");
      await loadSettings();
    } catch (err: any) {
      toast.error(err?.message ?? "Could not save your profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <PortalPage eyebrow="Account" title="Settings" intro="Profile, household, and portal status from your family account records.">
      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormCard title="Profile">
          <Field label="Full name">
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Smith" />
          </Field>
          <Field label="Email">
            <Input value={row?.email ?? user?.email ?? ""} disabled />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
          </Field>
          <Field label="Date of birth">
            <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
          </Field>
        </FormCard>
        <FormCard title="Household & Strategy">
          <Field label="Household size" hint="Total people in your household including you.">
            <Input type="number" min={1} max={20} value={form.household_size} onChange={(e) => setForm({ ...form, household_size: e.target.value })} placeholder="4" />
          </Field>
          <Field label="Target retirement age" hint="Used by the retirement horizon calculator.">
            <Input type="number" min={40} max={90} value={form.retirement_target_age} onChange={(e) => setForm({ ...form, retirement_target_age: e.target.value })} placeholder="65" />
          </Field>
          <Field label="Estimated net worth (USD)" hint="Used to surface estate-planning recommendations. Educational only.">
            <Input type="number" min={0} step={1000} value={form.estimated_net_worth} onChange={(e) => setForm({ ...form, estimated_net_worth: e.target.value })} placeholder="500000" />
          </Field>
          <Field label="Membership">
            <Input value={row?.tier_override ?? "Standard"} disabled />
          </Field>
        </FormCard>
        <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
          <p className="text-[12px] text-charcoal-500 mr-auto">
            Educational only — not legal, tax, or investment advice.
          </p>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save profile"}
          </PrimaryButton>
        </div>
      </form>
    </PortalPage>
  );
}

function FormCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
      <h3 className="font-display text-navy-800 text-[22px]">{title}</h3>
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-sans text-[12px] uppercase tracking-[0.16em] text-charcoal-500">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-charcoal-500">{hint}</p>}
    </div>
  );
}